import React, { useEffect, useState } from "react";
import { decryptData } from "../middleware/encryption";

/* -------------------------------------------------
   CONFIG – replace these with your actual endpoints
   ------------------------------------------------- */
const API_BASE = "https://upload.exellius.com"; // ← UPDATE (e.g. "https://api.myapp.com")
const TRACKING_ENDPOINT   = `${API_BASE}/api/import-dashboard/import-tracking`;   // returns [{id, file_name, status, queued_at, started_at, completed_at, error_message, insert_count, update_count}]

/* -------------------------------------------------
   Helper: fetch JSON with basic error handling
   ------------------------------------------------- */
async function fetchJson(url) {
  const token = sessionStorage.getItem("access_token");
  const headers = new Headers({
    Accept: "application/json",
  });

  if (token) {
    headers.append("Authorization", `Bearer ${token}`);
  }

  const resp = await fetch(url, {
    method: "GET",
    headers,
  });
  if (!resp.ok) {
    const txt = await resp.text();
    throw new Error(`❌ ${resp.status} – ${txt}`);
  }
  return resp.json().then((result) => {
    let decrypted = result;

    if (result && typeof result.data === "string") {
      decrypted = decryptData(result.data);
    } else if (result && result.data !== undefined) {
      decrypted = result.data;
    }

    if (decrypted && Array.isArray(decrypted.data)) {
      return decrypted.data;
    }

    return decrypted;
  });
}

/* -------------------------------------------------
   Main Dashboard Component
   ------------------------------------------------- */
export default function ImportDashboard() {
  const [tracking, setTracking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadAll() {
      try {
        const trackingResponse = await fetchJson(TRACKING_ENDPOINT);
        setTracking(Array.isArray(trackingResponse) ? trackingResponse : (trackingResponse?.rows || trackingResponse?.data || []));
      } catch (e) {
        console.error(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-state">
        <div className="dashboard-spinner" />
        Loading import activity…
      </div>
    );
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  const completedCount = tracking.filter((row) => row.status === "completed").length;
  const failedCount = tracking.filter((row) => row.status === "failed" || row.error_message).length;
  const processingCount = tracking.filter((row) => row.status === "processing" || row.status === "queued").length;

  return (
    <section className="dashboard">
      <div className="dashboard-heading">
        <div>
          <span className="dashboard-eyebrow">DATA MANAGEMENT</span>
          <h1>Import Dashboard</h1>
          <p>Monitor CSV imports, processing activity, and downloaded files.</p>
        </div>
      </div>

      <div className="dashboard-summary">
        <SummaryCard label="Total Imports" value={tracking.length} tone="purple" />
        <SummaryCard label="Completed" value={completedCount} tone="green" />
        <SummaryCard label="Failed" value={failedCount} tone="red" />
        <SummaryCard label="Processing" value={processingCount} tone="blue" />
        
      </div>

      <div className="dashboard-panel">
        <div className="panel-heading">
          <div>
            <h2>Import tracking</h2>
            <p>Latest activity across uploaded files</p>
          </div>
        </div>

        <div className="table-scroll">
          <table className="tracking-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Status</th>
                <th>Queued</th>
                <th>Started</th>
                <th>Completed</th>
                <th>Inserted</th>
                <th>Updated</th>
                <th>Completed CSV</th>
                <th>Failed CSV</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {tracking.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.file_name}</strong></td>
                  <td><span className={`status-badge status-${row.status}`}>{row.status}</span></td>
                  <td>{formatDate(row.queued_at)}</td>
                  <td>{formatDate(row.started_at)}</td>
                  <td>{formatDate(row.completed_at)}</td>
                  <td>{row.insert_count ?? 0}</td>
                  <td>{row.update_count ?? 0}</td>
                  <td>
                    <button
                      type="button"
                      className="csv-link csv-download-button"
                      onClick={() => downloadCsv("completed", `${row.id}.csv`, setError)}
                    >
                      {row.id}.csv
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="csv-link csv-link-failed csv-download-button"
                      onClick={() => downloadCsv("failed", `${row.id}.csv`, setError)}
                    >
                      {row.id}.csv
                    </button>
                  </td>
                  <td className="error-cell">{row.error_message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {!tracking.length && <div className="panel-empty">No import activity found.</div>}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------
   Summary Card Component
   ------------------------------------------------- */
function SummaryCard({ label, value, tone }) {
  return (
    <div className={`summary-card summary-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : "—";
}

const downloadCsv = async (type, fileName, onError) => {
  try {
    const token = sessionStorage.getItem("access_token");
    const headers = new Headers({ Accept: "text/csv" });

    if (token) {
      headers.append("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(
      `${API_BASE}/api/import-dashboard/${type}/${encodeURIComponent(fileName)}`,
      { method: "GET", headers }
    );

    if (!response.ok) {
      throw new Error(`Failed to download ${fileName}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (downloadError) {
    if (onError) onError(downloadError.message);
  }
};
